import { motion, AnimatePresence } from "framer-motion";
import { useMemo } from "react";
import { Play, Pause, SkipForward, SkipBack, X, Headphones, Loader2 } from "lucide-react";
import { usePodcast } from "../lib/PodcastContext";

/**
 * Iter-27.46 — Mini-player podcast sticky en bas d'écran.
 * Affiché uniquement quand un podcast est actif (state.isActive=true).
 * Mobile-first : compact, touch-friendly, progress bar visible.
 */
export default function PodcastPlayer() {
    const {
        isActive,
        isPlaying,
        isLoading,
        moduleTitle,
        currentItem,
        currentIdx,
        totalSlides,
        items,
        estimatedMinutes,
        currentTime,
        duration,
        playbackRate,
        togglePause,
        skipNext,
        skipPrev,
        stop,
        seek,
        setPlaybackRate,
    } = usePodcast();

    // Iter-30.56 — Calcule la position de la leçon + de la slide DANS la leçon
    // pour que l'apprenant voie où il en est sans devoir ouvrir le module.
    // Ex : "Leçon 2/4 · Slide 3/7" dans le mini-player.
    const lessonPosition = useMemo(() => {
        if (!items || items.length === 0) {
            return { lessonIdx: 0, totalLessons: 0, slideInLessonIdx: 0, slidesInLesson: 0 };
        }
        // Liste ordonnée et unique des lesson_id rencontrés dans l'ordre
        const lessonOrder = [];
        const slidesByLesson = new Map();
        items.forEach((it, idx) => {
            if (!slidesByLesson.has(it.lesson_id)) {
                slidesByLesson.set(it.lesson_id, []);
                lessonOrder.push(it.lesson_id);
            }
            slidesByLesson.get(it.lesson_id).push(idx);
        });
        const currentLessonId = items[currentIdx]?.lesson_id;
        const lessonIdx = lessonOrder.indexOf(currentLessonId);
        const slides = slidesByLesson.get(currentLessonId) || [];
        return {
            lessonIdx: Math.max(0, lessonIdx),
            totalLessons: lessonOrder.length,
            slideInLessonIdx: Math.max(0, slides.indexOf(currentIdx)),
            slidesInLesson: slides.length,
        };
    }, [items, currentIdx]);

    // Iter-27.50 — Cycle entre les 4 vitesses au clic
    const RATES = [1, 1.25, 1.5, 0.8];
    const cycleRate = () => {
        const idx = RATES.indexOf(playbackRate);
        const next = RATES[(idx + 1) % RATES.length];
        setPlaybackRate(next);
    };
    // Affichage compact : "1×" / "1,25×" / "1,5×" / "0,8×"
    const rateLabel = (() => {
        if (playbackRate === 1) return "1×";
        if (playbackRate === 0.8) return "0,8×";
        if (playbackRate === 1.25) return "1,25×";
        if (playbackRate === 1.5) return "1,5×";
        return `${playbackRate}×`;
    })();

    // Iter-27.48 — Format mm:ss pour le seekbar
    const fmt = (s) => {
        if (!isFinite(s)) return "0:00";
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <AnimatePresence>
            {isActive && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 320, damping: 30 }}
                    className="fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]"
                    data-testid="podcast-player"
                >
                    <div className="mx-auto max-w-3xl px-2 sm:px-4 pb-2 sm:pb-3">
                        <div className="glass-card rounded-2xl border border-shariti-orange/30 bg-shariti-bg/95 backdrop-blur-2xl shadow-[0_-12px_40px_rgba(238,114,46,0.18)] overflow-hidden">
                            {/* Iter-27.48 — Seekbar interactif (clic/drag pour scrubber l'audio courant) */}
                            <div className="px-3 pt-2 pb-1 sm:px-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-extrabold text-white/55 tabular-nums w-8 text-right">
                                        {fmt(currentTime)}
                                    </span>
                                    <div className="flex-1 relative group py-2 -my-2">
                                        <input
                                            type="range"
                                            min="0"
                                            max={Math.max(duration || 0, 0.1)}
                                            step="0.5"
                                            value={currentTime}
                                            onChange={(e) => seek(parseFloat(e.target.value))}
                                            data-testid="podcast-seekbar"
                                            disabled={!duration || isLoading}
                                            className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer disabled:cursor-not-allowed"
                                            title="Cliquer ou glisser pour naviguer dans la slide"
                                        />
                                        <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-shariti-orange to-shariti-orange-light transition-all duration-150"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                        {/* Indicateur drag — apparait au hover */}
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-shariti-orange shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                                            style={{ left: `calc(${progress}% - 6px)` }}
                                        />
                                    </div>
                                    <span className="text-[10px] font-extrabold text-white/55 tabular-nums w-8">
                                        {fmt(duration)}
                                    </span>
                                </div>
                                {/* Progress global du module : segmentée par slide */}
                                <div className="mt-1 h-0.5 bg-white/5 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-shariti-orange/40 transition-all duration-500"
                                        style={{
                                            width: totalSlides > 0
                                                ? `${((currentIdx + 1) / totalSlides) * 100}%`
                                                : "0%",
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="px-3 py-2 sm:px-4 sm:py-2.5 flex items-center gap-3">
                                {/* Icône & info */}
                                <div className="w-10 h-10 rounded-xl bg-shariti-orange/15 flex items-center justify-center shrink-0 relative">
                                    <Headphones className="w-4 h-4 text-shariti-orange-light" />
                                    {isPlaying && (
                                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-shariti-orange animate-pulse" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] font-thin tracking-[0.18em] uppercase text-shariti-orange-light truncate">
                                        Mode podcast · {moduleTitle}
                                    </p>
                                    <p className="text-sm font-extrabold text-white truncate" data-testid="podcast-current-label">
                                        {currentItem?.lesson_title || "—"} · {currentItem?.slide_label || "—"}
                                    </p>
                                    <p className="text-[10px] text-white/40 font-light mt-0.5 truncate" data-testid="podcast-position-label">
                                        {lessonPosition.totalLessons > 0 ? (
                                            <>
                                                Leçon {lessonPosition.lessonIdx + 1}/{lessonPosition.totalLessons}
                                                <span className="opacity-50 mx-1.5">·</span>
                                                Slide {lessonPosition.slideInLessonIdx + 1}/{lessonPosition.slidesInLesson}
                                                <span className="opacity-50 mx-1.5">·</span>
                                                ~{estimatedMinutes} min
                                            </>
                                        ) : (
                                            <>Slide {currentIdx + 1} / {totalSlides} · ~{estimatedMinutes} min total</>
                                        )}
                                    </p>
                                </div>

                                {/* Contrôles */}
                                <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                                    <button
                                        onClick={skipPrev}
                                        disabled={currentIdx === 0 || isLoading}
                                        data-testid="podcast-skip-prev"
                                        className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/12 text-white/75 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Slide précédente"
                                    >
                                        <SkipBack className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={togglePause}
                                        disabled={isLoading}
                                        data-testid="podcast-toggle"
                                        className="w-11 h-11 rounded-full flex items-center justify-center bg-shariti-orange hover:bg-shariti-orange-light text-white orange-glow transition-all disabled:opacity-60"
                                        title={isPlaying ? "Pause" : "Lire"}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : isPlaying ? (
                                            <Pause className="w-5 h-5" />
                                        ) : (
                                            <Play className="w-5 h-5 ml-0.5" />
                                        )}
                                    </button>
                                    <button
                                        onClick={skipNext}
                                        disabled={currentIdx >= totalSlides - 1 || isLoading}
                                        data-testid="podcast-skip-next"
                                        className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/12 text-white/75 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Slide suivante"
                                    >
                                        <SkipForward className="w-4 h-4" />
                                    </button>
                                    {/* Iter-27.50 — Bouton vitesse (cycle 1×→1,25×→1,5×→0,8×) */}
                                    <button
                                        onClick={cycleRate}
                                        data-testid="podcast-speed-toggle"
                                        className={`min-w-[42px] h-9 px-2 rounded-full flex items-center justify-center text-[11px] font-extrabold tabular-nums transition-all ${
                                            playbackRate !== 1
                                                ? "bg-shariti-yellow/15 text-shariti-yellow border border-shariti-yellow/40"
                                                : "bg-white/5 hover:bg-white/12 text-white/75 hover:text-white"
                                        }`}
                                        title={`Vitesse ${rateLabel} — cliquer pour changer`}
                                    >
                                        {rateLabel}
                                    </button>
                                    <button
                                        onClick={stop}
                                        data-testid="podcast-close"
                                        className="w-9 h-9 rounded-full flex items-center justify-center bg-white/3 hover:bg-red-500/15 text-white/45 hover:text-red-300 transition-all ml-1"
                                        title="Arrêter le podcast"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
